<template>
  <div class="new-conversation px-2 py-2 mb-1">
    <div v-if="isAdmin" class="search-wrapper">
      <div class="search-input p-relative">
        <div class="input-prepend p-absolute h-100 d-flex align-items-center">
          <v-icon icon="search" size="1.5rem" />
        </div>
        <v-select
          multiple
          deselect-from-dropdown
          :placeholder="
            $t('components.messageThread.newConversation.searchChatPlaceHolder')
          "
          label="name"
          :value="selectedSubscribers"
          :filterable="false"
          :close-on-select="false"
          :clear-search-on-blur="() => false"
          :clear-search-on-select="false"
          :options="subscribers"
          :dropdown-should-open="dropdownShouldOpen"
          @search:focus="handleFocus"
          @search:blur="handleBlur"
          @search="handleSearch"
          @input="handleSelected"
        >
          <template #option="item">
            <div class="d-flex align-items-center subscriber__result">
              <subscriber-avatar
                class="mr-1"
                x-small
                :subscriber="{
                  name: item.name,
                  email: item.email,
                  avatar_urls: item.avatar_urls
                }"
              />
              <div>
                <h4 class="subscriber__result-name my-0">{{ item.name }}</h4>
                <p class="my-0 text-x-small text-grey-70">{{ item.email }}</p>
              </div>
              <input
                type="checkbox"
                :checked="isSelectedSubscriber(item.id)"
                class="item-checkbox ml-auto"
                @click.stop="e => handleSubscriberInput(item, e.target.checked)"
              />
            </div>
          </template>

          <template #list-footer>
            <div
              v-if="selectedSubscribersCount > 0"
              class="footer d-flex justify-content-between align-items-center"
            >
              <subscriber-avatars
                :subscribers="selectedSubscribers"
                :limit="5"
                x-small
              />

              <v-button
                small
                primary
                :is-loading="isStartingChat"
                class="start-chat__button"
                @click="startChat"
              >
                {{
                  $t("components.messageThread.newConversation.chatWith", {
                    selectedSubscribersCount: selectedSubscribersCount
                  })
                }}
                {{ pluralize("Member", selectedSubscribersCount) }}
              </v-button>
            </div>
          </template>

          <template slot="no-options">
            <template v-if="isLoading">
              <div
                class="d-flex align-items-center justify-content-center w-100"
              >
                <v-loader />
              </div>
            </template>
            <template v-else>
              {{
                $t(
                  "components.messageThread.newConversation.subscriberNotFound"
                )
              }}
            </template>
          </template>
        </v-select>

        <v-button
          v-if="selectedSubscribers.length"
          ghost
          round
          class="p-absolute search-input__dismiss"
          @click="clearSearch"
        >
          <v-icon icon="close" />
        </v-button>
      </div>
    </div>
    <div v-else-if="adminMessagingProfiles.length > 1" class="search-wrapper">
      <div class="search-input p-relative">
        <div class="input-prepend p-absolute h-100 d-flex align-items-center">
          <v-icon icon="forum" size="1.5rem" />
        </div>
        <v-select
          multiple
          deselect-from-dropdown
          placeholder="Tap to start a chat"
          label="name"
          :value="selectedSubscribers"
          :filterable="false"
          :close-on-select="false"
          :clear-search-on-blur="() => false"
          :clear-search-on-select="false"
          :options="adminMessagingProfiles.map(profile => profile.subscriber)"
          :dropdown-should-open="dropdownShouldOpen"
          @search:focus="handleFocus"
          @search:blur="handleBlur"
          @input="handleSelected"
        >
          <template #option="item">
            <div class="d-flex align-items-center subscriber__result">
              <subscriber-avatar
                class="mr-1"
                x-small
                :subscriber="{
                  name: item.name,
                  email: item.email,
                  avatar_urls: item.avatar_urls
                }"
              />
              <div>
                <h4 class="subscriber__result-name my-0">{{ item.name }}</h4>
                <p class="my-0 text-x-small text-grey-70">{{ item.email }}</p>
              </div>
              <input
                type="checkbox"
                :checked="isSelectedSubscriber(item.id)"
                class="item-checkbox ml-auto"
                @click.stop="e => handleSubscriberInput(item, e.target.checked)"
              />
            </div>
          </template>

          <template #list-footer>
            <div
              v-if="selectedSubscribersCount > 0"
              class="footer d-flex justify-content-between align-items-center"
            >
              <subscriber-avatars
                :subscribers="selectedSubscribers"
                :limit="5"
                x-small
              />

              <v-button
                small
                primary
                :is-loading="isStartingChat"
                class="start-chat__button"
                @click="startChat"
              >
                {{
                  $t("components.messageThread.newConversation.chatWith", {
                    selectedSubscribersCount:
                      selectedSubscribers.length > 1
                        ? selectedSubscribers.length
                        : selectedSubscribers[0].name.split(" ")[0]
                  })
                }}
                {{
                  selectedSubscribers.length > 1
                    ? pluralize("Admins", selectedSubscribersCount)
                    : ""
                }}
              </v-button>
            </div>
          </template>

          <template slot="no-options">
            <template v-if="isLoading">
              <div
                class="d-flex align-items-center justify-content-center w-100"
              >
                <v-loader />
              </div>
            </template>
            <template v-else>
              {{
                $t(
                  "components.messageThread.newConversation.subscriberNotFound"
                )
              }}
            </template>
          </template>
        </v-select>

        <v-button
          v-if="selectedSubscribers.length"
          ghost
          round
          class="p-absolute search-input__dismiss"
          @click="clearSearch"
        >
          <v-icon icon="close" />
        </v-button>
      </div>
    </div>
    <v-button
      v-else
      class="w-100"
      :is-loading="isStartingChat"
      @click="startChatWithAdmin"
    >
      <v-icon icon="chat" class="mr-1" />
      {{
        $t("components.messageThread.newConversation.message", {
          appName: app.name
        })
      }}
    </v-button>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex"
import { pluralize } from "@/lib/utils"
import vSelect from "vue-select"

import ApiClient from "@/lib/api-client"
import { isPresent } from "@/lib/utils"

export default {
  components: {
    vSelect,
    SubscriberAvatar: () => import("@/components/SubscriberAvatar"),
    SubscriberAvatars: () => import("@/components/SubscriberAvatars")
  },

  props: {
    isAdmin: { type: Boolean, required: true }
  },

  data() {
    return {
      isLoading: false,
      isFocused: false,
      isStartingChat: false,
      selectedSubscribers: [],
      subscribers: [],
      value: ""
    }
  },

  computed: {
    ...mapState("auth", ["user"]),
    ...mapState("messages", ["adminThread", "adminMessagingProfiles"]),
    ...mapGetters(["app"]),

    selectedSubscribersCount() {
      return this.selectedSubscribers.length
    }
  },

  deactivated() {
    if (this.isAdmin) this.selectedSubscribers = []
  },

  mounted() {
    if (!this.isAdmin) this.fetchMessagingProfiles()
  },

  methods: {
    isPresent,
    pluralize,
    ...mapActions("messages", [
      "fetchSubscribers",
      "fetchAdminThread",
      "fetchMessagingProfiles"
    ]),

    handleFocus() {
      this.isFocused = true
      this.isLoading = true
      this.handleSearch("", () => {})
    },

    handleBlur() {
      this.isFocused = false
    },

    handleSearch(value, loading) {
      clearTimeout(this.debounce)
      this.debounce = setTimeout(() => {
        this.isLoading = true
        loading(true)

        ApiClient.get("/subscribers", {
          params: { term: value }
        })
          .then(res => {
            this.subscribers = res.data.subscribers
          })
          .finally(() => {
            loading(false)
            this.isLoading = false
            this.value = value
          })
      }, 600)
    },

    isSelectedSubscriber(subscriber_id) {
      return !!this.selectedSubscribers.find(s => s.id == subscriber_id)
    },

    handleSelected(subscribers) {
      this.selectedSubscribers = subscribers
    },

    clearSearch() {
      this.selectedSubscribers = []
    },

    handleSubscriberInput(subscriber, checked) {
      const existingSubscriber = this.selectedSubscribers.find(
        s => s.id == subscriber.id
      )

      if (existingSubscriber && !checked) {
        this.selectedSubscribers = this.selectedSubscribers.filter(
          s => s.id != subscriber.id
        )
      } else if (!existingSubscriber) {
        this.selectedSubscribers.push(subscriber)
      }
    },

    startChatWithAdmin() {
      // TODO: Remove in favor of adminMessagingProfiles
      if (this.adminThread?.thread_id) {
        this.$router.push(`/me/messages/${this.adminThread.thread_id}`)
      } else {
        this.isStartingChat = true

        this.fetchAdminThread()
          .then(({ thread_id, subscriber_id }) => {
            this.$router.push(
              thread_id
                ? `/me/messages/${thread_id}`
                : `/me/messages/new?users=${subscriber_id}`
            )
          })
          .finally(() => {
            this.isStartingChat = false
          })
      }
    },

    startChat() {
      // Although the "users" query param is used, it refers to the `subscriber_id` not the `user_id`
      // Confusing, I know... but its more user friendly for those snooping in the URL
      if (this.selectedSubscribersCount < 1) return

      const selectedSubscriberIds = this.selectedSubscribers.map(s => s.id)

      this.isStartingChat = true
      this.fetchSubscribers(selectedSubscriberIds)
        .then(({ private_thread_id }) => {
          this.$router.push(
            private_thread_id
              ? `/me/messages/${private_thread_id}`
              : `/me/messages/new?users=${selectedSubscriberIds.join(",")}`
          )
        })
        .finally(() => (this.isStartingChat = false))
    },

    dropdownShouldOpen() {
      return this.selectedSubscribers.length || this.isFocused
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";
@import "~vue-select/dist/vue-select.css";

$search-input--border-radius: 0.375rem;

.new-conversation {
  .heading {
    font-weight: 600;
  }

  .footer {
    background-color: $color-ui--grey-10;
    position: sticky;
    bottom: -$size--2;
    padding: $size--3 $size--4;
    margin: 0 - $size--2;

    .start-chat__button {
      padding: $size--3 $size--4;
      font-size: 13px;
    }
  }
}

.subscriber__result {
  .subscriber__result-name {
    font-size: $size--4;
    line-height: $font-size--base;
    font-weight: $font-weight--bold;
  }
}

.search-input {
  @include block-link;

  position: relative;
  border-radius: $search-input--border-radius;

  .input-prepend {
    padding-left: $size--2;
    color: $color-ui--grey-60;
    z-index: 10;
  }

  .vs__no-options {
    padding: 1rem 0;
    margin: 0;
    font-size: 1rem;
    font-weight: 500;
    color: $color-ui--grey-60;
  }

  .vs__actions {
    .vs__open-indicator {
      display: none;
    }

    .vs__spinner {
      border-width: 3px;
    }
  }

  .vs__dropdown-toggle {
    min-height: $input--base-height - 4px;
    padding: 0 $size--8;
    border: none;

    .vs__search {
      margin-top: 0;

      &::placeholder {
        color: $color-ui--grey-60;
        font-size: $font-size--small;
      }
    }

    .vs__selected-options {
      align-items: center;

      .vs__selected {
        height: 28px;
        font-size: $font-size--small;
        font-weight: $font-weight--semi-bold;
        background: transparent;
        border: none;
        padding: $size--2;
      }

      .vs__deselect {
        margin-left: $size--2;
      }
    }
  }

  .vs__dropdown-menu {
    padding: $size--2;
    margin-top: -2px;
    box-shadow: $box-shadow--medium--no-bleed;
    border: 2px solid var(--brand-color-hex);
    border-top: 0;
    border-radius: $search-input--border-radius;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    color: $color-ui--dark-blue;
    width: calc(100% + 4px);
    left: -2px;
    height: calc(
      60vh - 120px
    ); // 50% of viewport height minus the height of the header and search box

    .vs__dropdown-option {
      padding: $size--2;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .vs__dropdown-option--highlight {
      background-color: $color-ui--grey-10;
      border-radius: 4px;
      color: $color-ui--dark-blue;
    }

    .item-checkbox {
      transform: scale(1.5);
      padding: 10px;
      accent-color: $color-ui--dark-blue;
      transition: transform 0.2s;
      cursor: pointer;

      &:hover {
        transform: scale(1.6);
      }
    }
  }
}

.search-input__dismiss {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10;
}
</style>
